import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";
import MediaImage from "@/components/MediaImage";
import EmptyState from "@/components/EmptyState";
import { SignalBadge, playerLink } from "@/components/PlayerCard";
import { useSeo } from "@/seo";
import { milestoneLabel } from "@/milestones";

const RANK_COLORS = ["text-[#FFD600]", "text-slate-100", "text-[#CD7F32]"];

export default function Leaderboard() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  useSeo(t("seo.leaderboard_title"), t("seo.leaderboard_desc"), "/leaderboard");

  useEffect(() => {
    api.get("/leaderboard").then((r) => setData(r.data)).catch(() => setData({ entries: [] }));
  }, []);

  if (!data) {
    return <p className="pt-16 text-center font-mono2 text-sm text-slate-500">{t("common.loading")}</p>;
  }

  return (
    <div className="pt-6" data-testid="leaderboard-page">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#FFD600]/40 bg-[#FFD600]/10">
          <Trophy className="h-5 w-5 text-[#FFD600]" />
        </span>
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight text-slate-50 sm:text-4xl">{t("lb.title")}</h1>
          <p className="text-xs text-slate-400 sm:text-sm">{t("lb.sub")}</p>
          <Link to="/hype" data-testid="hype-explainer-link-lb" className="mt-1 inline-block font-mono2 text-[10px] uppercase tracking-wider text-[#00F0FF] hover:underline">{t("hype.link")}</Link>
        </div>
      </div>

      {data.entries.length === 0 ? (
        <EmptyState testid="leaderboard-empty" title={t("lb.title")} body={t("lb.empty")} />
      ) : (
        <ol className="space-y-3">
          {data.entries.map((e, i) => {
            const p = e.player;
            const topReason = e.signal?.reasons?.[0];
            return (
              <li key={`${p.provider}:${p.player_id}`} data-testid="leaderboard-row">
                <Link to={playerLink(p)} className="hud-card hud-card-hover flex items-center gap-4 rounded-xl p-4">
                  <span className={`w-10 shrink-0 text-center font-display text-4xl font-black ${RANK_COLORS[i] || "text-slate-600"}`} data-testid={`leaderboard-rank-${i + 1}`}>
                    {i + 1}
                  </span>
                  <MediaImage
                    src={p.club_logo}
                    alt={t("media.logo_alt", { club: p.club_name || "—" })}
                    kind="club" size={48} surface="leaderboard"
                    imgClassName="object-contain p-1.5"
                    testid={`leaderboard-media-${p.player_id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-display text-xl font-semibold uppercase tracking-tight text-slate-50">{p.name}</h3>
                    <p className="truncate text-xs text-slate-400">{p.club_name} · {p.league_name}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {e.recent_milestones.slice(0, 3).map((m) => (
                        <span key={`${m.type}-${m.date}`} className="rounded-full border border-[#00FF87]/40 bg-[#00FF87]/10 px-2 py-0.5 font-mono2 text-[9px] uppercase tracking-wider text-[#00FF87]" data-testid="leaderboard-milestone-chip">
                          {milestoneLabel(m, t)} · {m.date}
                        </span>
                      ))}
                      {topReason && (
                        <span className="hidden truncate text-[11px] text-slate-500 lg:inline">
                          {t(`reasons.${topReason.key}`, topReason.params || {})}
                        </span>
                      )}
                    </div>
                  </div>
                  <SignalBadge score={p.signal_score ?? null} testid={`leaderboard-score-${i + 1}`} />
                </Link>
              </li>
            );
          })}
        </ol>
      )}
      {data.computed_at && (
        <p className="mt-6 text-center font-mono2 text-[10px] uppercase tracking-wider text-slate-600">
          {t("lb.computed")}: {new Date(data.computed_at).toLocaleString(lang === "fr" ? "fr-FR" : "en-GB")}
        </p>
      )}
    </div>
  );
}
